import type { DepthQuery } from '@/schemas/depth'

/**
 * Query key factory.
 *
 * The book keys on sector, the chosen symbols, page and page size — all four
 * decide which books come back, and `symbols` in particular is an array
 * whose order the grid honours, so it belongs in the key rather than beside
 * it. TanStack Query hashes keys structurally, so an equal selection built
 * twice is one cache entry.
 *
 * NOT on `sortBy`/`sortDesc`, even though `DepthQuery` carries them —
 * `depthQuerySchema` extends the shared `tableQuerySchema`, which is where
 * those two fields come from, but `depthService.depth()` never reads either
 * one (there is no sort control on this screen; the grid runs A→Z by symbol).
 * Spreading the whole query object into the key the way `book` used to would
 * let a value that changes nothing about the response still force a full
 * refetch of every book on the page.
 *
 * The sector list and each sector's names are separate and never invalidated
 * with the books: they are the page's navigation, not its data.
 */
export const depthKeys = {
  all: ['depth'] as const,
  books: () => [...depthKeys.all, 'book'] as const,
  book: (query: DepthQuery) =>
    [
      ...depthKeys.books(),
      { sector: query.sector, symbols: query.symbols, page: query.page, pageSize: query.pageSize },
    ] as const,
  sectors: () => [...depthKeys.all, 'sectors'] as const,
  sectorCompanies: (sectorId: string) => [...depthKeys.all, 'sector-companies', sectorId] as const,
}
